import React, { useState, useEffect } from 'react';
import {
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { chamadoAssuntoService, ChamadoAssunto } from '../../services/chamadoAssuntoService';

interface ChamadoAssuntoSelectProps {
  value?: number | null;
  onChange: (assuntoId: number | null, assunto?: ChamadoAssunto) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  allowClear?: boolean;
}

const ChamadoAssuntoSelect: React.FC<ChamadoAssuntoSelectProps> = ({
  value = null,
  onChange,
  label = 'Assunto do chamado',
  placeholder = 'Selecione o assunto',
  required = false,
  disabled = false,
  error = false,
  helperText,
  size = 'small',
  fullWidth = true,
}) => {
  const [assuntos, setAssuntos] = useState<ChamadoAssunto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await chamadoAssuntoService.getAll();
        if (!cancelled && res.success && res.data) {
          setAssuntos(res.data);
        }
      } catch {
        if (!cancelled) setAssuntos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const selectedOption = assuntos.find(a => a.id === value) || null;

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      error={error}
      disabled={disabled || loading}
      required={required}
    >
      <Autocomplete
        size={size}
        options={assuntos.sort((a, b) => {
          const catA = a.categoria?.nome || 'Sem Categoria';
          const catB = b.categoria?.nome || 'Sem Categoria';
          return catA.localeCompare(catB);
        })}
        groupBy={(option) => option.categoria?.nome || 'Sem Categoria'}
        getOptionLabel={(option) => option.nome}
        value={selectedOption}
        onChange={(_, newValue) => {
          onChange(newValue ? newValue.id : null, newValue || undefined);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {option.categoria?.cor && (
                <Box sx={{ width: 10, height: 10, bgcolor: option.categoria.cor, borderRadius: '50%' }} />
              )}
              <Typography variant="body2">{option.nome}</Typography>
            </Box>
          </Box>
        )}
      />
    </FormControl>
  );
};

export default ChamadoAssuntoSelect;
